// ============================================================================
// src/app/boot/deferredAuthIntake.ts — get session intake OUT of the auth lock
// ============================================================================
// `sb.auth.onAuthStateChange(cb)` looks like an ordinary event subscription. It
// is not. GoTrue invokes `cb` from INSIDE its exclusive auth lock and AWAITS it:
//
//   // auth-js GoTrueClient._notifyAllSubscribers
//   const promises = Array.from(this.stateChangeEmitters.values())
//     .map(async (x) => { await x.callback(event, session) })
//   await Promise.all(promises)
//
// So any Supabase call the callback awaits re-enters the lock it is already
// holding. A PostgREST query does exactly that without looking like it:
// supabase-js attaches the access token via `_getAccessToken()`, which calls
// `auth.getSession()`, which calls `_acquireLock`.
//
// AND IT DOES NOT TIME OUT. The obvious reading — "the 5s lockAcquireTimeout
// will break it" — is wrong. That timeout guards only the OUTER acquisition.
// A re-entrant acquisition takes this branch instead:
//
//   if (this.lockAcquired) {
//     const last = this.pendingInLock.length
//       ? this.pendingInLock[this.pendingInLock.length - 1]
//       : Promise.resolve()
//     const result = (async () => { await last; return await fn() })()
//     ...
//   }
//
// `last` is the tail of `pendingInLock` — the notify chain the callback is
// running inside. The nested call awaits the work that is waiting on it. Both
// promises stay pending forever.
//
// THE PROFILE BUDGET DOES NOT FIX THIS. src/app/boot/sessionProfileLoad.ts
// bounds the caller, so the app reaches PROFILE_STATUS.FAILED instead of
// hanging — but 20 seconds later, and having resolved nothing. Escaping the
// lock is what makes the profile read succeed.
//
// auth-js documents the hazard and prescribes this exact remedy in its
// `onAuthStateChange` remarks — "Do not use other Supabase functions in the
// callback function. If you must, dispatch the functions once the callback has
// finished executing" — with `setTimeout(..., 0)` as the shown mechanism.
//
// A MACROTASK, NOT A MICROTASK — and the reason is narrower than it looks.
//
// Returning synchronously is what breaks the self-await: `_acquireLock`
// drains `pendingInLock` before releasing, so once the callback returns the
// nested re-entrant wait resolves. Measured against the installed client, a
// synchronous callback plus `queueMicrotask` therefore COMPLETES rather than
// hanging.
//
// What it still does is run while the lock is HELD: the microtask queue
// drains inside the drain loop, so the work re-enters the lock and extends
// the critical section. A macrotask runs after the lock is released. That is
// the difference deferredAuthIntake.test.ts measures — by sampling auth-js's
// own `lockAcquired` flag — and it is what Supabase's guidance prescribes.
//
// SINGLE-SLOT, AND A STALE RUN DOES NOTHING
//
// Deferral turns one synchronous callback into a queue, and a queue has an
// order the caller did not previously have to think about. Two positive events
// can arrive before either deferred run executes; the newest is the current
// session and the older one is stale. So scheduling REPLACES any run still
// waiting, and a run that fires anyway — because a cancel handle did not take —
// returns without executing its work and without clearing the newer slot.
// There is deliberately no append-mode method for that ordering bug to come
// back through.
//
// It knows nothing about sessions, profiles or roles. It moves a function out
// of a callback, remembers that it did, and keeps only the newest.
// ============================================================================

/**
 * Schedules `run` on a future macrotask and returns a cancel handle.
 * Injectable so a suite can drive the timing deterministically — and so the
 * insufficient microtask variants can be substituted and shown to fail.
 */
export type ScheduleMacrotask = (run: () => void) => () => void;

const defaultSchedule: ScheduleMacrotask = (run) => {
  const timer = setTimeout(run, 0);
  return () => clearTimeout(timer);
};

export type DeferredAuthIntake = {
  /**
   * Run `work` after the current callback has returned and the lock is free,
   * CANCELLING any run still waiting to fire.
   */
  deferLatest: (work: () => void) => void;
  /** Drop the scheduled run if it has not fired. Sign-out and effect cleanup. */
  cancelAll: () => void;
  /** 1 while a run is scheduled and unfired, otherwise 0. */
  pending: () => number;
};

export function createDeferredAuthIntake(schedule: ScheduleMacrotask = defaultSchedule): DeferredAuthIntake {
  // The entry is installed BEFORE scheduling, and carries a mutable cancel,
  // because a synchronous test scheduler runs `work` during `schedule(...)` —
  // before it has returned a handle to store. `current` is already this entry
  // by then, so the wrapper can clear it either way.
  type Entry = {cancel: (() => void) | null};
  let current: Entry | null = null;

  const cancelCurrent = () => {
    const entry = current;
    current = null;
    entry?.cancel?.();
  };

  return {
    deferLatest(work: () => void) {
      cancelCurrent();
      const entry: Entry = {cancel: null};
      current = entry;
      entry.cancel = schedule(() => {
        // A run that is no longer the current slot does NOTHING: it neither
        // executes its work nor clears the slot that replaced it. Executing it
        // would land an older intake after a newer one; clearing the slot would
        // report pending() === 0 while a newer run is still scheduled, so a
        // later cancelAll() would have nothing to cancel.
        if (current !== entry) return;
        current = null;
        work();
      });
    },
    cancelAll: cancelCurrent,
    pending: () => (current === null ? 0 : 1),
  };
}
